import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  saveDataToLocalStorage,
  getDataFromLocalStorage,
  removeFromLocalStorage,
} from 'helpers';
import { IProductInfo, ISaveOrderItemInfo } from 'interfaces';
import { pos365Hooks } from 'hooks';
import { Modal } from 'antd';
import { useNavigate } from 'react-router-dom';

const { confirm: showSuccessModal } = Modal;

interface CartContextState {
  cartData: IProductInfo[];
  addToCart: (param: IProductInfo) => void;
  deleteProductFromCart: (id: number) => void;
  totalPrice: number;
  getCurrentProductQuantity: (id?: number) => number;
  getCurrentProductNote: (id: number) => string;
  totalItem: number;
  deleteAllFromCart: () => void;
  saveOrder: () => Promise<void>;
  isSavingOrder: boolean;
}

interface CartProviderProps {
  roomCode: string;
  children: React.ReactNode;
}

const defaultCartContext: CartContextState = {
  cartData: [],
  addToCart: () => {},
  deleteProductFromCart: () => {},
  totalPrice: 0,
  getCurrentProductQuantity: () => 0,
  getCurrentProductNote: () => '',
  totalItem: 0,
  deleteAllFromCart: () => {},
  saveOrder: async () => {},
  isSavingOrder: false,
};

const CartContext = createContext<CartContextState>(defaultCartContext);

const LOCAL_STORAGE_CART_KEY_PREFIX = 'pinpos-order-cart-data';

const checkValidCartData = (strValue: string): boolean => {
  try {
    const arr = JSON.parse(strValue);
    if (!Array.isArray(arr)) return false;
    return arr.every(el => {
      return (
        // check has these properties
        el.hasOwnProperty('id') &&
        el.hasOwnProperty('name') &&
        el.hasOwnProperty('quantity') &&
        el.hasOwnProperty('note') &&
        el.hasOwnProperty('price') &&
        el.hasOwnProperty('images') &&
        // validate these properties
        typeof el.quantity === 'number' &&
        typeof el.price === 'number' &&
        Array.isArray(el.images)
      );
    });
  } catch (_) {
    return false;
  }
};

const { usePos365 } = pos365Hooks;

export const CartProvider: React.FC<CartProviderProps> = ({
  roomCode,
  children,
}: CartProviderProps) => {
  const { saveOrder: pos365SaveOrder } = usePos365();
  const navigate = useNavigate();

  const [cartData, setCartData] = useState<IProductInfo[]>([]);
  const [isSavingOrder, setIsSavingOrder] = useState<boolean>(false);

  const LOCAL_STORAGE_CART_KEY = `${LOCAL_STORAGE_CART_KEY_PREFIX}-${roomCode}`;

  const addToCart = (addToCartParam: IProductInfo) => {
    let newCartData: IProductInfo[];

    const currentProductIndex = cartData.findIndex(
      product => product.id === addToCartParam.id
    );

    // case add new product to cart
    if (currentProductIndex === -1) {
      newCartData = [...cartData, addToCartParam];
    } else {
      // filter out old product info
      newCartData = [
        ...cartData.slice(0, currentProductIndex),
        addToCartParam,
        ...cartData.slice(currentProductIndex + 1, cartData.length),
      ];
    }

    setCartData(newCartData);
    saveDataToLocalStorage(LOCAL_STORAGE_CART_KEY, JSON.stringify(newCartData));
  };

  const deleteProductFromCart = (id: number) => {
    let newCartData: IProductInfo[];

    const currentProduct = cartData.find(product => product.id === id);

    if (!currentProduct) {
      return;
    } else {
      newCartData = [...cartData.filter(product => product.id !== id)];
    }

    setCartData(newCartData);
    saveDataToLocalStorage(LOCAL_STORAGE_CART_KEY, JSON.stringify(newCartData));
  };

  const deleteAllFromCart = () => {
    setCartData([]);
    saveDataToLocalStorage(LOCAL_STORAGE_CART_KEY, JSON.stringify([]));
  };

  const getCurrentProductQuantity = (id?: number): number => {
    if (!id) return 0;
    return cartData.find(product => product.id === id)?.quantity || 0;
  };

  const getCurrentProductNote = (id: number): string => {
    return cartData.find(product => product.id === id)?.note || '';
  };

  const saveOrder = async (): Promise<void> => {
    try {
      setIsSavingOrder(true);
      const items: ISaveOrderItemInfo[] = cartData.map(data => ({
        productId: data.id,
        name: data.name,
        quantity: data.quantity,
        description: data.note,
      }));

      await pos365SaveOrder(roomCode, items);

      deleteAllFromCart();
      showSuccessModal({
        title: 'Yêu cầu gọi món thành công',
        content:
          'Yêu cầu của bạn đã được ghi nhận, vui lòng đợi cửa hàng xử lý trong ít phút!',
        cancelText: 'Gọi thêm món',
        onCancel() {
          navigate(`/?roomCode=${roomCode}`);
        },
        okText: 'Xem món đã gọi',
        onOk() {
          navigate(`/order?roomCode=${roomCode}`);
        },
      });
    } catch (e) {
    } finally {
      setIsSavingOrder(false);
    }
  };

  // get cart data from local storage
  useEffect(() => {
    const localStorageCartData = getDataFromLocalStorage(
      LOCAL_STORAGE_CART_KEY
    );

    if (
      typeof localStorageCartData === 'string' &&
      checkValidCartData(localStorageCartData)
    ) {
      setCartData(JSON.parse(localStorageCartData) as IProductInfo[]);
    } else {
      // if not valid, remove it
      removeFromLocalStorage(LOCAL_STORAGE_CART_KEY);
    }
  }, []);

  return (
    <CartContext.Provider
      value={{
        cartData,
        addToCart,
        deleteProductFromCart,
        deleteAllFromCart,
        get totalPrice() {
          return cartData.reduce(
            (acc, curr) => acc + curr.price * curr.quantity,
            0
          );
        },
        getCurrentProductQuantity,
        getCurrentProductNote,
        get totalItem() {
          return cartData.length;
        },
        saveOrder,
        isSavingOrder,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  return useContext(CartContext);
};
